import { type FC, type MutableRefObject, type UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  type CommentResolutionFilterOption,
  type CommentVisibilityFilterOption,
  type NodeComment,
} from '@/types/comments';
import { useCommentStore } from '@/hooks/data/comments/useCommentStore';
import { Comment } from '@/components/inspector/comments/comment';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const CommentsList: FC<{
  commentsArePending: boolean;
  commentsAreFetching: boolean;
  comments: Array<NodeComment>;
  resolutionFilter?: CommentResolutionFilterOption;
  visibilityFilter?: CommentVisibilityFilterOption;
  readOnly?: boolean;
  nodeId?: string;
  fetchNextPage?: () => Promise<unknown>;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  scrollContainerRef?: MutableRefObject<HTMLDivElement | null>;
  onScroll?: () => void;
}> = ({
  commentsArePending,
  comments,
  resolutionFilter,
  visibilityFilter,
  readOnly,
  nodeId,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  scrollContainerRef,
  onScroll: onScrollProp,
}) => {
  const [openCommentMenuId, setOpenCommentMenuId] = useState<string | null>(null);
  const internalViewportRef = useRef<HTMLDivElement | null>(null);
  const pruneCommentState = useCommentStore((state) => state.pruneCommentState);

  // Combine internal ref with external ref
  const setViewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      internalViewportRef.current = node;
      if (scrollContainerRef) {
        scrollContainerRef.current = node;
      }
    },
    [scrollContainerRef],
  );

  const tComments = useTranslations('inspector.comments');

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      // Call parent's scroll handler for tracking scroll position
      onScrollProp?.();

      if (!hasNextPage || isFetchingNextPage || !fetchNextPage) {
        return;
      }

      const target = event.currentTarget;
      const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
      if (distanceFromBottom < 64) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage, onScrollProp],
  );

  useEffect(() => {
    const validCommentIds = comments.flatMap((comment) => [
      comment.commentId,
      ...comment.latestReplies.map((reply) => reply.id),
    ]);

    pruneCommentState(validCommentIds);
  }, [comments, pruneCommentState]);

  return (
    // Loader and empty state are inside the scroll area because we don't want bottom
    // elements (comment input and action buttons) to go out of view on shorter screens.
    // This way even if user types in a really long comment and empty state is shown,
    // comment composer won't go out of view
    <ScrollArea
      viewportRef={setViewportRef}
      className={cn('min-h-5 grow bg-ui-container')}
      {...(comments.length === 0
        ? commentsArePending
          ? {
              disabled: true,
              viewportClassName:
                'flex size-full flex-col justify-center bg-input-background pointer-events-none opacity-50',
            }
          : {
              viewportClassName: 'flex items-center size-full',
            }
        : {})}
      onScroll={handleScroll}
    >
      {commentsArePending && comments.length === 0 ? (
        <Loader2 className="animate-spin justify-self-center stroke-icon-base" />
      ) : comments.length === 0 ? (
        <p className="px-4 py-2 text-center text-balance text-icon-base">
          {!resolutionFilter && !visibilityFilter ? tComments('noComments') : tComments('noCommentsForCurrentFilter')}
        </p>
      ) : (
        <div className="flex flex-col space-y-3 p-3">
          {comments.map((comment) => (
            <Comment
              key={comment.commentId}
              comment={comment}
              readOnly={readOnly}
              openCommentMenuId={openCommentMenuId}
              setOpenCommentMenuId={setOpenCommentMenuId}
              nodeId={nodeId}
              scrollContainerRef={internalViewportRef}
            />
          ))}
          {isFetchingNextPage && (
            <div className="flex items-center justify-center gap-2 p-4 text-xs text-text-gray">
              <Loader2 className="icon-6 animate-spin" />
            </div>
          )}
        </div>
      )}
    </ScrollArea>
  );
};
